import React, { useState, useMemo } from 'react';
import { Calendar, Clock, Loader2, Lock, Truck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { useTasks } from '../../context/TasksContext';

// 30-min time slots from 6 AM to 8 PM
const TIME_SLOTS = Array.from({ length: 29 }, (_, i) => {
  const totalMinutes = 6 * 60 + i * 30;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});

const DURATIONS = [
  { label: '30 min',   value: 30  },
  { label: '1 hour',   value: 60  },
  { label: '1.5 hours',value: 90  },
  { label: '2 hours',  value: 120 },
  { label: '2.5 hours',value: 150 },
  { label: '3 hours',  value: 180 },
  { label: '4 hours',  value: 240 },
  { label: '5 hours',  value: 300 },
  { label: '6 hours',  value: 360 },
];

const formatTimeLabel = (t) => {
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${m === 0 ? '00' : '30'} ${period}`;
};

// Parse ISO string to date ("YYYY-MM-DD") and time ("HH:MM") strings
const parseIso = (iso) => {
  if (!iso) return { date: '', time: '09:00' };
  try {
    const d = new Date(iso);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const h = String(d.getHours()).padStart(2, '0');
    const m = d.getMinutes() < 30 ? '00' : '30';
    const time = `${h}:${m}`;
    return { date, time };
  } catch {
    return { date: '', time: '09:00' };
  }
};

// Calculate duration in minutes from two ISO strings (rounded to nearest 30)
const calcDurationMins = (startIso, endIso) => {
  if (!startIso || !endIso) return 60;
  const ms = new Date(endIso) - new Date(startIso);
  if (ms <= 0) return 60;
  const mins = Math.round(ms / (60 * 1000));
  const options = DURATIONS.map((d) => d.value);
  return options.reduce((prev, cur) =>
    Math.abs(cur - mins) < Math.abs(prev - mins) ? cur : prev
  );
};

/**
 * RescheduleJobModal
 *
 * Allows admins and dispatchers to change a job's scheduled date/time AND
 * its scheduling mode (appointment ↔ route_flexible). Pre-populates the
 * mode from the existing task's `jobMode` (defaults to 'appointment' when
 * the existing task has a real time-of-day, else 'route_flexible').
 *
 * Status is intentionally preserved — this is a schedule edit, not a
 * status transition.
 */
const RescheduleJobModal = ({ task, isOpen, onClose }) => {
  const { rescheduleTask } = useTasks();

  const initial = useMemo(() => {
    const { date, time } = parseIso(task?.scheduledStart);
    const duration = calcDurationMins(task?.scheduledStart, task?.scheduledEnd);
    // Prefer the stored jobMode; if absent, infer from data shape:
    //   - had a non-midnight time-of-day → appointment
    //   - otherwise default to route_flexible
    const storedMode = task?.jobMode;
    const inferredMode = (() => {
      if (storedMode === 'appointment' || storedMode === 'route_flexible') return storedMode;
      if (task?.hasTime === false) return 'route_flexible';
      if (task?.hasTime === true)  return 'appointment';
      // Last resort: real ISO with hour/minute > 0 → appointment
      if (task?.scheduledStart) {
        try {
          const d = new Date(task.scheduledStart);
          if (d.getHours() !== 0 || d.getMinutes() !== 0) return 'appointment';
        } catch { /* fall through */ }
      }
      return 'route_flexible';
    })();

    return {
      date: date || (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      })(),
      time: time || '09:00',
      duration,
      mode: inferredMode,
    };
  }, [task?.scheduledStart, task?.scheduledEnd, task?.jobMode, task?.hasTime]);

  const [date, setDate]           = useState(initial.date);
  const [startTime, setStartTime] = useState(initial.time);
  const [duration, setDuration]   = useState(initial.duration);
  const [jobMode, setJobMode]     = useState(initial.mode);
  // Sprint 1 — every schedule change carries reason + optional note so the
  // Activity Timeline shows WHY the visit moved.
  const [reason, setReason]       = useState('customer_request');
  const [note, setNote]           = useState('');
  const [saving, setSaving]       = useState(false);

  // Re-sync when task changes (e.g., opened for a different task)
  React.useEffect(() => {
    if (isOpen) {
      setDate(initial.date);
      setStartTime(initial.time);
      setDuration(initial.duration);
      setJobMode(initial.mode);
      setReason('customer_request');
      setNote('');
    }
  }, [isOpen, initial.date, initial.time, initial.duration, initial.mode]);

  const handleReschedule = async () => {
    if (!task || !date) return;
    if (jobMode === 'appointment' && !startTime) return;
    setSaving(true);
    try {
      const [year, month, day] = date.split('-').map(Number);
      const isRoute = jobMode === 'route_flexible';
      const [hour, minute] = isRoute ? [0, 0] : startTime.split(':').map(Number);
      const newStart = new Date(year, month - 1, day, hour, minute, 0, 0);
      const newEnd   = isRoute ? null : new Date(newStart.getTime() + duration * 60 * 1000);

      // Canonical schedule-change path. Centralizes task write + history
      // append + recurring-cache mirror in one atomic batch.
      const result = await rescheduleTask(task.id, {
        newScheduledStart: newStart.toISOString(),
        newScheduledEnd:   newEnd ? newEnd.toISOString() : null,
        newJobMode:        jobMode,
        newHasTime:        !isRoute,
        reason,
        note: note.trim() || undefined,
      });

      const dateLabel = newStart.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
      });
      const cadenceSuffix = result?.recurringCursorSynced
        ? ' · Future visits will roll automatically'
        : '';
      if (isRoute) {
        toast.success(`Job rescheduled to ${dateLabel} · Route stop${cadenceSuffix}`);
      } else {
        const timeLabel = newStart.toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit', hour12: true,
        });
        toast.success(`Job rescheduled to ${dateLabel} at ${timeLabel}${cadenceSuffix}`);
      }
      onClose();
    } catch (err) {
      console.error('[RescheduleJobModal] Error rescheduling job:', err);
      toast.error('Failed to reschedule job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Reschedule Job
          </DialogTitle>
        </DialogHeader>

        {task && (
          <div className="text-sm text-gray-600 -mt-1">
            <span className="font-semibold text-gray-900">{task.title || 'Job'}</span>
            {task.scheduledStart && (
              <span className="ml-1 text-gray-400 text-xs">
                · currently {new Date(task.scheduledStart).toLocaleDateString('en-US', {
                  weekday: 'short', month: 'short', day: 'numeric',
                })}
              </span>
            )}
          </div>
        )}

        <Separator />

        <div className="space-y-4">
          {/* Mode toggle — appointment commits a customer time;
              route-flexible is a date-only stop planned by Build Day. */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              How is this job scheduled?
            </label>
            <div className="grid grid-cols-2 rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setJobMode('appointment')}
                className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium border-r border-gray-200 transition-colors ${
                  jobMode === 'appointment'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
                data-testid="reschedule-mode-appointment"
              >
                <Lock className="h-3 w-3" />
                Fixed Appointment
              </button>
              <button
                type="button"
                onClick={() => setJobMode('route_flexible')}
                className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
                  jobMode === 'route_flexible'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
                data-testid="reschedule-mode-route"
              >
                <Truck className="h-3 w-3" />
                Route Stop
              </button>
            </div>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              {jobMode === 'appointment'
                ? 'Customer receives a specific arrival time.'
                : 'Customer receives a service date only. Dispatch and Build Day determine the route sequence and ETA later.'}
            </p>
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              New Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="reschedule-date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            />
          </div>

          {/* Start Time — hidden in route_flexible mode */}
          {jobMode === 'appointment' && (
            <div className="space-y-1.5" data-testid="reschedule-time-block">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                Start Time
              </label>
              <select
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                data-testid="reschedule-time"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              >
                {TIME_SLOTS.map((t) => (
                  <option key={t} value={t}>{formatTimeLabel(t)}</option>
                ))}
              </select>
            </div>
          )}

          {/* Duration — only meaningful when appointment commits a time block */}
          {jobMode === 'appointment' && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Duration
              </label>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                data-testid="reschedule-duration"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              >
                {DURATIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Sprint 1 — reason + note ride along on every schedule change */}
          <div className="space-y-1.5 col-span-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Why is this moving?
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="reschedule-reason"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            >
              <option value="customer_request">Customer request</option>
              <option value="weather">Weather</option>
              <option value="technician">Technician availability</option>
              <option value="office">Office decision</option>
              <option value="system">System / automation</option>
            </select>
          </div>

          <div className="space-y-1.5 col-span-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Note <span className="font-normal normal-case text-gray-400">(optional, shows on the customer timeline)</span>
            </label>
            <input
              type="text"
              value={note}
              maxLength={280}
              placeholder="e.g. customer travelling, asked for Friday"
              onChange={(e) => setNote(e.target.value)}
              data-testid="reschedule-note"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleReschedule}
            disabled={saving || !date || (jobMode === 'appointment' && !startTime)}
            data-testid="reschedule-confirm-btn"
            className="text-white bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              'Confirm Reschedule'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleJobModal;
